import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { useLocaleInfo } from "@/hooks/useLocaleInfo";

export function useScrollCarousel(initialIndex: number) {
  const ref = useRef<HTMLUListElement>(null);
  const rafId = useRef<number | null>(null);
  const { dir } = useLocaleInfo();

  const [index, setIndex] = useState(initialIndex);

  // RTL browsers report negative scrollLeft values, so negate the offset
  // when scrolling to the initial position programmatically.
  useLayoutEffect(() => {
    if (ref.current === null) return;

    const isRTL = dir === "rtl";
    const offset = ref.current.clientWidth * initialIndex;
    ref.current.scrollLeft = isRTL ? -offset : offset;
  }, [initialIndex, dir]);

  useEffect(() => {
    return () => {
      if (rafId.current !== null) {
        window.cancelAnimationFrame(rafId.current);
      }
    };
  }, []);

  const handleScroll = useCallback(() => {
    // Skip if a frame is already scheduled to avoid redundant state updates.
    if (rafId.current !== null) return;

    rafId.current = window.requestAnimationFrame(() => {
      rafId.current = null;

      if (ref.current === null) return;

      const { scrollLeft, clientWidth } = ref.current;
      // Math.abs converts RTL's negative scrollLeft to a positive offset.
      const newIndex = Math.round(Math.abs(scrollLeft) / clientWidth);

      setIndex((prev) => (newIndex === prev ? prev : newIndex));
    });
  }, []);

  const changeIndex = useCallback(
    (targetIndex: number) => {
      if (ref.current === null) return;

      const isRTL = dir === "rtl";
      const offset = ref.current.clientWidth * targetIndex;

      ref.current.scrollTo({
        left: isRTL ? -offset : offset,
        behavior: "smooth",
      });
    },
    [dir],
  );

  return { ref, index, handleScroll, changeIndex };
}
